/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from "@emotion/react";
import { useState, useCallback, useMemo, useEffect } from "react";
import { useToast } from "../../hooks";
import { useMeasure } from "react-use";

const toastCSS = css`
    align-items: center;
    background: #6eac9d;
    border-radius: 4px;
    box-shadow: 0px 4px 8px rgba(28, 27, 88, 0.3);
    color: #ffffff;
    display: flex;
    font-size: 15px;
    font-weight: 600;
    height: 48px;
    justify-content: center;
    line-height: 24px;
    padding: 0 16px;
    width: 100%;
    pointer-events: all;
`;
const toastsCSS = ({ isToastsGreatherThan450, isToastsGreatherThan650 }) => css`
    bottom: 0;
    left: 0;
    padding: 16px;
    position: absolute;
    width: 100%;
    pointer-events: none;
    display: grid;
    row-gap: 16px;

    ${isToastsGreatherThan450 &&
        `
        padding: 24px;
    `}

    ${isToastsGreatherThan650 &&
        `
        padding: 36px;
    `}
`;

export const usePlannerToasts = () => {
    const [toastsContainerRef, { width: toastsContainerWidth }] = useMeasure();
    const isToastsGreatherThan450 = toastsContainerWidth >= 450;
    const isToastsGreatherThan650 = toastsContainerWidth >= 650;
    const renderToast = useCallback(props => {
        const { message } = props;
        return <span css={toastCSS}>{message}</span>;
    }, []);
    const [showToast, Toasts, toastsProps] = useToast({ defaultTimeout: 1500, renderToast });
    const _toastsProps = useMemo(
        () => ({ ...toastsProps, css: toastsCSS({ isToastsGreatherThan450, isToastsGreatherThan650 }) }),
        [toastsProps, isToastsGreatherThan450, isToastsGreatherThan650],
    );

    return [showToast, Toasts, _toastsProps, toastsContainerRef];
};

export const useAutoFollow = ({ isAutoFollowEnabled, stopsById, createFitBounds, setViewport, useTwoColumnLayout }) => {
    const [activeSuggestionId, setActiveSuggestionId] = useState(null);

    useEffect(() => {
        if (!activeSuggestionId || !isAutoFollowEnabled) {
            return;
        }
        const geojsonObject = stopsById[activeSuggestionId].placeOutline;
        const fitBounds = createFitBounds(geojsonObject);
        const mapboxEl = document.querySelector(".js__mapbox-gl");
        if (!mapboxEl) {
            return;
        }
        const mapSize = mapboxEl.getBoundingClientRect();
        fitBounds({ mapSize, onViewportChange: setViewport, useTwoColumnLayout, useOldLayout: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSuggestionId, isAutoFollowEnabled]);

    return { activeSuggestionId, setActiveSuggestionId };
};
